import { ref, onMounted, watch, nextTick } from 'vue';
import { useSlabData } from './composables/useSlabData';
import { useSlabFilters, DEFAULT_STONE_TYPES_LAST } from './composables/useSlabFilters';
import { useSlabUrl } from './composables/useSlabUrl';
import { useSlabUpload } from './composables/useSlabUpload';
import { debounce } from './utils/search';
import { createImageLoadingHelpers } from './utils/imageLoading';
import { soundex, soundexWithSpaces, titleCase } from './utils/text';
import { useFavoritesHandler } from '../favorites-handler/favoritesHandler';

/**
 * Slab Viewer Composable (Main)
 * Coordinates data fetching, filtering, and upload modules.
 */
export function useSlabViewer(props = {}) {
    const settings = props.settings || {};
    const dealerOnTop = props.dealerOnTop || false;
    /** `true` uses the default stone types; an array overrides them. */
    const stoneTypesLast = props.stoneTypesLast === true
        ? DEFAULT_STONE_TYPES_LAST
        : (Array.isArray(props.stoneTypesLast) ? props.stoneTypesLast : []);

    // 1. Initialize Modules
    const dataModule = useSlabData(props);
    const urlModule = useSlabUrl();
    const filtersModule = useSlabFilters(dataModule.items, {
        stockCount: dataModule.stockCount,
        availableCount: dataModule.availableCount
    }, urlModule);

    const uploadModule = useSlabUpload(dataModule.items, dataModule.allItems, filtersModule);

    // Favorites Integration
    const favHandler = useFavoritesHandler();
    const isLoggedIn = ref(false);

    const isFavorite = (item) => {
        // Assuming 'block' as default type for slab/block items
        return favHandler.isFavorite('block', item.id);
    };



    const {
        search, activeFilters, selectedOrder, loadedImages,
        toggleNewArrivals, toggleSaleFilter, toggleSingleFilter, toggleIncludeHolds, toggleFilter,
        toggleTitle, toggleMobileFilter, toggleDealers, toggleDomestically,
        toggleSignature, toggleStock, clearFilters
    } = filtersModule;

    // Reactivity for feature flags
    const hasDealerItems = ref(false);
    const hasSignatureItems = ref(false);
    const hasStockItems = ref(false);

    // Track loading state for specific items
    const favoriteLoading = ref(new Set());

    const isFavoriteLoading = (item) => favoriteLoading.value.has(item.id);

    /**
     * Flips a slab's favourite state. The handler shows the new state at once,
     * sends one request per slab and folds extra taps into it, so there is no
     * tap to block here: `favoriteLoading` only reports that a save is settling.
     */
    const toggleFavorite = async (item) => {
        const type = 'block';

        favoriteLoading.value.add(item.id);

        try {
            const result = await favHandler.toggleFavorite(type, item.id);

            if (result.status === 'failed') {
                console.error("Toggle favorite failed, reverting UI", result.error);
            }
        } finally {
            if (!favHandler.isFavoritePending(type, item.id)) {
                favoriteLoading.value.delete(item.id);
            }
        }
    };

    // 2. UI Helper Methods
    const listThumbnail = (item) => {
        if (!item?.thumbnail) {
            return '';
        }

        return item.thumbnail.replace('slab.jpg', 'small.jpg');
    };

    const {
        isImageLoaded,
        onImageLoad,
        onImageError,
        checkImageDimensions,
        syncPreloadedImagesFromDom,
    } = createImageLoadingHelpers(loadedImages);

    watch(() => filtersModule.filteredItems.value, () => {
        nextTick(() => syncPreloadedImagesFromDom());
    });

    const getSlabFilterClass = (filter) => {
        setTimeout(() => {
            document.querySelectorAll(".filter-item.not-textured")[0]?.classList.add("not-textured-0");
            document.querySelectorAll(".filter-item.textured")[0]?.classList.add("textured-0");
        }, 100);

        if (filter.meta) {
            if (filter.meta.textured === true) return "textured";
            if (filter.meta.textured !== true) return "not-textured";
        }
    };

    const getTileFilterClass = (filter) => {
        setTimeout(() => {
            document.querySelectorAll(".filter-item.not-textured")[0]?.classList.add("not-textured-0");
            document.querySelectorAll(".filter-item.textured")[0]?.classList.add("textured-0");
        }, 100);

        if (filter.meta) {
            if (filter.meta.textured === true) return "textured";
            if (filter.meta.textured !== true) return "not-textured";
        }
    };

    // 3. Main Init Logic
    const searchProducts = debounce(() => {
        filtersModule.updateFilters();
    }, 1000);

    const init = async () => {
        document.querySelectorAll('.slide').forEach(element => element.classList.remove('stage1'));
        dataModule.loading.value = true;

        if (settings.saleFilter) filtersModule.useSaleFilter.value = settings.saleFilter;

        // Start favourites alongside the slab list so hearts do not wait for it.
        // A page token gets the full handler; otherwise the stored token or the
        // guest session is enough to read the favourites cart alone.
        const tokenMeta = document.querySelector('meta[name="api-token"]');
        if (tokenMeta && tokenMeta.getAttribute('content')) {
            isLoggedIn.value = true;
            favHandler.init();
        } else {
            favHandler.loadFavorites().catch(error => console.error('Failed to load favourites', error));
        }

        const itemsData = await dataModule.loadData();

        // Expose to window for debugging
        window.jsViewer = {
            search, items: dataModule.items, filters: filtersModule.filters,
            filterKeys: filtersModule.filterKeys, filteredItems: filtersModule.filteredItems,
            activeFilters, updateFilters: filtersModule.updateFilters
        };

        if (dealerOnTop) {
            filtersModule.showDealerOnTop.value = true;
        }

        filtersModule.stoneTypesLast.value = stoneTypesLast;
        filtersModule.dependentFilters.value = settings.dependentFilters || {};

        hasDealerItems.value = itemsData.some(item => item.dealer_product);
        hasSignatureItems.value = itemsData.some(item => item.signature_product);
        hasStockItems.value = itemsData.some(item => item.stocked_locally);

        filtersModule.filters.value = filtersModule.buildFilters(dataModule.items.value);
        filtersModule.filterKeys.value = Object.keys(filtersModule.filters.value);

        try {
            const toggles = {
                onlyNewArrivals: filtersModule.onlyNewArrivals,
                onlySales: filtersModule.onlySales,
                onlySingles: filtersModule.onlySingles,
                singlesExact: filtersModule.singlesExact,
                onlyDoubles: filtersModule.onlyDoubles,
                onlyDealerItems: filtersModule.onlyDealerItems,
                onlySignatureItems: filtersModule.onlySignatureItems,
                onlyStockItems: filtersModule.onlyStockItems,
                onlyDomesticItems: filtersModule.onlyDomesticItems,
                includeHolds: filtersModule.includeHolds,
            };
            const dimensions = {
                minWidth: filtersModule.minWidth,
                maxWidth: filtersModule.maxWidth,
                minHeight: filtersModule.minHeight,
                maxHeight: filtersModule.maxHeight,
            };
            const counts = {
                stockCount: dataModule.stockCount,
                availableCount: dataModule.availableCount,
            };
            urlModule.loadStateFromUrl({
                search,
                activeFilters,
                filters: filtersModule.filters,
                filterKeys: filtersModule.filterKeys,
                toggles,
                dimensions,
                counts,
                order: selectedOrder
            });
        } catch (error) {
            console.log("Failed to load state from URL");
        }

        filtersModule.updateFilters();

        await nextTick();
        syncPreloadedImagesFromDom();

        document.querySelectorAll('.slide').forEach(element => element.classList.remove('loading'));
        dataModule.loading.value = false;

        setTimeout(() => {
            document.querySelector(".filter-intro")?.addEventListener("click", () => {
                console.log("Filter intro clicked");
                document.querySelector(".filters .filter-contents")?.classList.toggle("hidden");
            });

            // Focus search input unless user navigated back
            const navEntries = performance.getEntriesByType("navigation");
            const isBackNavigation = navEntries.length > 0 && navEntries[0].type === "back_forward";

            if (!isBackNavigation) {
                document.querySelector(".psearch")?.focus();
            }
        }, 100);
    };

    // 4. Return merged state
    return {
        ...dataModule,
        ...filtersModule,
        ...uploadModule,
        // Helpers
        titleCase, listThumbnail, isImageLoaded, onImageLoad, onImageError, checkImageDimensions,
        getSlabFilterClass, getTileFilterClass, soundex, soundexWithSpaces,
        searchProducts, init,
        // Flags
        hasDealerItems, hasSignatureItems, hasStockItems,
        isLoggedIn, toggleFavorite, isFavorite, favoriteLoading, isFavoriteLoading,
    };
}

/**
 * Create a component definition that uses inline template from the DOM
 */
export function createSlabViewerComponent(props = {}) {
    return {
        setup() {
            const viewer = useSlabViewer(props);

            onMounted(() => {
                viewer.init();
            });

            watch(viewer.search, () => {
                viewer.searchProducts();
            });

            return viewer;
        }
    };
}
